import { useEffect } from "react";
import { Box, Text } from "@chakra-ui/layout";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import {
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  Flex,
  TabPanel,
  Link,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import theme from "@chakra-ui/theme";
import React, { useState } from "react";
import { useSession } from "next-auth/react";
import ProtectedRoute from "components/protected-route";
import { Page } from "components/page";
import {
  PeoplePanel,
  BestForYouPanel,
  ReplyToCommentsPanel,
  WithMyCircle,
} from "components/engage";
import { color, textStyle } from "theme/names";
import { useRouter } from "next/dist/client/router";
import { getAccount } from "utils/sessionHelper";
import { UpgradePopup } from "components/upgrade-popup";
import { getPlan } from "controllers/subscription";

let startLimitationUTCHour = 12;
let endLimitationUTCHour = 19;
let utcHours = new Date().getUTCHours();
var isPeakHour =
  utcHours >= startLimitationUTCHour && utcHours < endLimitationUTCHour;

let tabs = [
  "best-for-you",
  "reply-to-comments",
  "people",
  "organizations",
  "with-my-circle",
];

const tabIconContent = {
  "best-for-you": "These posts were selected for you by our AI engine",
  "reply-to-comments": "reply-to-comments",
  people:
    "Use Engage to interact with other people’s content and get noticed. Either let Taplio provide a selection or use your own lists of contacts.",
  organizations:
    "Engage on LinkedIn posts by people in your organization to boost their performance.",
  "with-my-circle":
    "Build stronger relationships with those who interacted with your posts in the last 14 days. We may sample the posts we retrieve to maintain performance.",
};

const Engage = ({ query }) => {
  const { data: session, status } = useSession() ?? {};
  const loading = status === "loading";
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [engageInfoIconText, setEngageInfoIconText] = useState(
    "These posts were selected for you by our AI engine for you"
  );

  // let isValidToken = getAccount(session)?.thWriteAccessToken && (getAccount(session)?.thApp === "T_TWEETHUNTERWRITE" || !isPeakHour);
  const router = useRouter();

  const handleTabChange = (tabIndex = 0) => {
    setTabIndex(tabIndex);
    setEngageInfoIconText(tabIconContent[tabs[tabIndex]]);
    router.push(
      {
        pathname: "/engage",
        query: { tab: tabs[tabIndex] },
      },
      undefined,
      { shallow: false }
    );
  };

  const changeTabFromQuery = (query) => {
    if (query.tab) {
      let match = tabs.find((x) => x == query.tab);
      if (match) {
        let index = tabs.indexOf(match);
        setTabIndex(index);
      }
    }
  };

  useEffect(() => {
    console.log("change tab to: " + query.tab);
    changeTabFromQuery(query);
  }, [query.tab]);

  const plan = getPlan(session?.user);
  const isStarterPlan = plan === "start";

  if (!loading && !session) {
    return <ProtectedRoute />;
  }

  const miscellaneousContext: any = useMiscellaneousContext();

  return (
    <Page
      title="Engage"
      isLoadingFeature={miscellaneousContext.isDataRefreshing}
      showLoadingBar={true}
      description="Engage with other people to attract more opportunities."
      descriptionWrapperProps={{ pt: "4", pb: "5" }}
      contentOnRightOfTitle={
        <Flex mb={0}>
          <Tooltip
            bg={useColorModeValue("white", "#1E1E1E")}
            color={useColorModeValue("#1E1E1E", "white")}
            rounded="lg"
            p={4}
            border={"1px solid " + theme.colors.purple[800]}
            placement="right"
            label={
              {
                start: (
                  <Box>
                    <Text textStyle={textStyle["body.medium.light"]}>
                      You are on the starter plan.{" "}
                      <Link href="/pricing" textDecoration="underline">
                        Upgrade to the Standard or Pro plan
                      </Link>{" "}
                      to comment in the engage section.
                    </Text>
                  </Box>
                ),
                grow: (
                  <Box>
                    <Text textStyle={textStyle["body.medium.light"]}>
                      Remaining credits this month to comment in the engage
                      section. Will reset the 1st of each month.
                    </Text>
                    <Text textStyle={textStyle["body.medium.light"]} mt={2}>
                      If you regularly hit that limit, we suggest you increase
                      it to 500 / month by{" "}
                      <Link href="/pricing" textDecoration="underline">
                        upgrading to the pro plan
                      </Link>
                      .
                    </Text>
                  </Box>
                ),
                enterprise: (
                  <Text textStyle={textStyle["body.medium.light"]}>
                    Remaining credits this month to comment in the engage
                    section. Will reset the 1st of each month.
                  </Text>
                ),
              }[session?.user?.data?.subscription?.plan ?? "starter"]
            }
          >
            {/* <InfoOutlineIcon /> */}
            <Button
              as="a"
              variant="outline"
              colorScheme="taplio"
              href="/pricing"
            >
              {/* {(session?.user?.data?.creditsReplies ?? 0) +
              " / " +
                (session?.user?.data?.subscription?.plan === "enterprise" ? 1500 : 100)} */}
              {`${session?.user?.data?.creditsReplies ?? 0} / ${{ enterprise: 1500, grow: 100, start: 0 }[
                session?.user?.data?.subscription?.plan ?? "start"
              ]
                }`}
            </Button>
          </Tooltip>
        </Flex>
      }
      infoTooltipText={engageInfoIconText}
    >
      {isStarterPlan ? (
        <UpgradePopup
          currentPlan={plan}
          feature="Engage"
          availablePlans={["grow", "enterprise"]}
        />
      ) : (
        <>
          <Flex justifyContent="space-between">
            <Flex
              gap={3}
              flexDirection={{ base: "column", md: "row" }}
              alignItems="center"
            >
              <Flex alignItems="center">
                {/* <Heading variant={variant.Heading.pageTitle} as="h1">
                    Engage
                  </Heading> */}
                {/* <Tooltip placement="right" hasArrow label={engageInfoIconText}>
                    <InfoOutlineIcon ml={2} mt={1} />
                  </Tooltip> */}
              </Flex>
              {/* <UpgradeToProCard creditsAi={accountInfo?.creditsAi} user={session?.user} /> */}
            </Flex>
          </Flex>
          <Tabs
            pt="8"
            index={tabIndex}
            onChange={(index) => {
              handleTabChange(index);
            }}
            isLazy
          >
            <TabList gap={2}>
              <Tab>Best for you</Tab>
              <Tab
                isDisabled={
                  getAccount(session)?.subscription?.plan == "starter"
                }
              >
                Reply to comments
              </Tab>
              <Tab>People</Tab>
              <Tooltip label="Create an organization or join one to easily engage with of people who engaged with posts from your organization.">
                {getAccount(session)?.subscription?.isInOrg ? (
                  <Tab isDisabled={!getAccount(session)?.subscription?.isInOrg}>
                    Organization
                  </Tab>
                ) : (
                  <Button
                    color={useColorModeValue(
                      color["state.lightMode.inactive"],
                      color["state.darkMode.inactive"]
                    )}
                    variant="ghost"
                    as="a"
                    href="/settings?tab=team"
                      >
                    Organization
                  </Button>
                )}
                {/* <Tab isDisabled={true}>Organization</Tab> */}
              </Tooltip>
              {getAccount(session)?.isAdmin && <Tab>My Circle</Tab>}
            </TabList>
            <TabPanels mt={5}>
              <TabPanel px="0">
                {tabIndex == tabs.indexOf("best-for-you") && (
                  <BestForYouPanel />
                )}
              </TabPanel>
              <TabPanel px="0">
                {tabIndex == tabs.indexOf("reply-to-comments") && (
                  <ReplyToCommentsPanel />
                )}
              </TabPanel>
              <TabPanel px="0">
                <PeoplePanel />
              </TabPanel>
              <TabPanel px="0">
                {/* <PeoplePanel isOrg={true} /> */}
                {tabIndex == tabs.indexOf("organizations") && (
                  <BestForYouPanel
                    isOrg={true}
                    header={<Text>{tabIconContent["organizations"]}</Text>}
                  />
                )}
              </TabPanel>
              {getAccount(session)?.isAdmin && (
                <TabPanel px="0">
                  <WithMyCircle header={tabIconContent["with-my-circle"]} />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </>
      )}
    </Page>
  );
};

Engage.getInitialProps = async ({ query }) => {
  // console.log(query);
  return { query };
};

export default Engage;
